import { useEffect, useRef, useState } from 'react';
import type { Product } from '@/api/models';
import { GarmentVisual } from '@/components/garment/GarmentVisual';
import { SizeButtons } from '@/components/product/SizeButtons';
import { useAddToCart } from '@/hooks/useAddToCart';
import { formatMoney } from '@/lib/money';
import { retrigger } from '@/lib/retrigger';

interface Props {
  product: Product | null;
  open: boolean;
  onClose: () => void;
  onDetails: (product: Product, size: string | null) => void;
}

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * A single piece pulled off the rail: it turns slowly in 3D (drag to spin it),
 * with sizes and "Add to cart" underneath. If "Add to cart" is pressed before
 * a size is chosen, the next size tapped goes straight into the cart.
 */
export function FocusView({ product, open, onClose, onDetails }: Props) {
  const [size, setSize] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [hint, setHint] = useState('');
  const [added, setAdded] = useState(false);
  const { addToCart, adding } = useAddToCart();

  const root = useRef<HTMLDivElement>(null);
  const card = useRef<HTMLDivElement>(null);
  const faces = useRef<(HTMLDivElement | null)[]>([]);
  const sizesRef = useRef<HTMLDivElement>(null);
  const angle = useRef(-25);
  const dragging = useRef(false);
  const lastX = useRef(0);

  // Fresh state for each piece.
  useEffect(() => {
    setSize(null); setPending(false); setHint(''); setAdded(false);
    angle.current = -25;
  }, [product?.id]);

  // Keep the hidden overlay out of the tab order; move focus in when it opens.
  useEffect(() => {
    if (root.current) root.current.inert = !open;
    if (open) {
      const t = window.setTimeout(() => root.current?.querySelector<HTMLElement>('.sizes button:not(:disabled), .factions .add')?.focus({ preventScroll: true }), 300);
      return () => window.clearTimeout(t);
    }
  }, [open, product?.id]);

  // Escape closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // The turntable.
  useEffect(() => {
    if (!open) return;
    let raf = 0;
    let last = performance.now();
    const still = reducedMotion();
    const tick = (t: number) => {
      const dt = Math.min(50, t - last);
      last = t;
      if (!dragging.current && !still) angle.current += dt * 0.04;
      const c = Math.abs(Math.cos((angle.current * Math.PI) / 180));
      if (card.current) card.current.style.transform = `rotateY(${angle.current}deg)`;
      // Brightness goes on the faces: a filter on the card would flatten the 3D turn.
      faces.current.forEach((f) => { if (f) f.style.filter = `brightness(${0.82 + 0.18 * c})`; });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [open]);

  if (!product) return <div ref={root} className="focus" aria-hidden="true" />;

  const hasSizes = product.sizes.length > 0;

  const doAdd = async (s: string) => {
    const ok = await addToCart(product, s, 1);
    if (ok) {
      setAdded(true);
      window.setTimeout(() => setAdded(false), 1600);
    }
  };

  const onAdd = () => {
    if (adding) return;
    if (hasSizes && !size) {
      setPending(true);
      retrigger(sizesRef.current, 'shake');
      setHint('Tap a size and it goes straight into your cart');
      return;
    }
    void doAdd(size ?? '');
  };

  const onSize = (s: string) => {
    setSize(s);
    setHint('');
    if (pending) { setPending(false); void doAdd(s); }
  };

  return (
    <div
      ref={root}
      className="focus"
      role="dialog"
      aria-label={product.name}
      aria-hidden={!open}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="spin"
        onPointerDown={(e) => { dragging.current = true; lastX.current = e.clientX; e.currentTarget.setPointerCapture(e.pointerId); }}
        onPointerMove={(e) => { if (!dragging.current) return; angle.current += (e.clientX - lastX.current) * 0.6; lastX.current = e.clientX; }}
        onPointerUp={() => { dragging.current = false; }}
        onPointerCancel={() => { dragging.current = false; }}
      >
        <div className="card" ref={card}>
          <div className="face front" ref={(el) => { faces.current[0] = el; }}>
            <GarmentVisual garment={product.garment} images={product.images} face="front" eager sizes="(max-width: 600px) 70vw, 320px" />
          </div>
          <div className="face back" ref={(el) => { faces.current[1] = el; }}>
            <GarmentVisual garment={product.garment} images={product.images} face="back" decorative sizes="(max-width: 600px) 70vw, 320px" />
          </div>
        </div>
      </div>

      <div className="fmeta">
        <div className="fcat">{product.category?.name}</div>
        <h2 className="fname">{product.name.toUpperCase()}</h2>
        <div className="fsub">{product.colorName ? `${product.colorName}, ` : ''}{formatMoney(product.price, product.currency)}</div>
        {hasSizes && (
          <>
            <div className="slabel" aria-hidden="true">Size</div>
            <SizeButtons sizes={product.sizes} value={size} onChange={onSize} highlight={pending} shakeRef={sizesRef} />
          </>
        )}
        <div className="hint" role="alert">{hint}</div>
      </div>

      <div className="factions">
        <button className="details" onClick={() => onDetails(product, size)}>Details</button>
        <button className={'add' + (added ? ' ok' : '')} onClick={onAdd} disabled={!product.inStock} aria-busy={adding}>
          {!product.inStock ? 'Sold out' : adding ? 'Adding…' : added ? 'Added to bag ✓' : 'Add to cart'}
        </button>
      </div>
    </div>
  );
}
